import { Divider, Tooltip, Typography } from "antd";
import { PieChart } from "components/Charts";
import { LoanSchemaDto } from "types/loan.types";
import { formatMoney } from "utils/functions";

const CREDITOR_COLORS = ["#a855f7", "#10b981", "#eab308", "#ef4444", "#6b7280"];

type T_LoanForPieData = Pick<LoanSchemaDto, "_id" | "due_amount" | "creditor_name">;
function sortAndAggregateByDueAmount(
  data: T_LoanForPieData[],
): { label: string; value: number; color: string; id: string }[] {
  // Sort the array by due_amount in descending order
  const sorted = [...data].sort((a, b) => (b?.due_amount ?? 0) - (a?.due_amount ?? 0));

  // Determine if we need an "Others" entry
  const hasOthers = sorted.length > 4;

  // Slice the first 4 elements
  const topFour = sorted.slice(0, 4);

  // Calculate the sum of the remaining due amounts if needed
  const othersTotal = hasOthers ? sorted.slice(4).reduce((sum, item) => sum + (item?.due_amount ?? 0), 0) : 0;

  // Map the first 4 elements to the desired format
  const result = topFour.map((item, i) => ({
    label: item.creditor_name,
    value: item.due_amount ?? 0,
    color: CREDITOR_COLORS[i],
    id: item._id,
  }));

  // Add the "Others" entry if necessary
  if (hasOthers) {
    result.push({ label: "Others", value: othersTotal, color: CREDITOR_COLORS[4], id: "Others" });
  }

  return result;
}

// export type LoanPieChartData =
export default function LoanPieChart({
  loanData,
  currentDebt,
}: {
  loanData: T_LoanForPieData[] | undefined;
  currentDebt: number | undefined;
}) {
  const loansForPieChart = loanData ? sortAndAggregateByDueAmount(loanData) : undefined;
  // console.log({ loansForPieChart, currentDebt });

  if (!loansForPieChart || !currentDebt) return null;
  // console.log("here");
  return (
    <>
      <Divider />
      <div className="h-[140px] grid items-center" style={{ gridTemplateColumns: "140px 1fr" }}>
        <div className="h-[140px]">
          <PieChart data={loansForPieChart} />
        </div>
        <div className="flex flex-col gap-1">
          {loansForPieChart.map((el) => {
            return (
              <div key={el.label} className="grid gap-1 items-center" style={{ gridTemplateColumns: "24px auto 30px" }}>
                <div className="size-6 flex-shrink-0 rounded-sm" style={{ backgroundColor: el.color }} />
                <Typography.Paragraph ellipsis={{ rows: 1, tooltip: el.label }} className="!text-sm !m-0">
                  {el.label}
                </Typography.Paragraph>
                <Tooltip title={formatMoney(el.value)}>
                  <Typography.Text type="secondary" className="!text-xs !font-semibold whitespace-nowrap tabular-nums">
                    {(el.value / currentDebt).toLocaleString("en-US", {
                      style: "percent",
                    })}
                  </Typography.Text>
                </Tooltip>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
